import React, { useEffect, useState } from 'react';
import { Container } from '@hpstellar/core/visId';

import CartHeader from './components/cart-header';
import OrderSummary from './components/order-summary';
import CartItems from './components/cart-items';
import ProductRecommendations from './components/product-recommendations';
import RewardsInfo from './components/rewards-info';
import DeliveryOptions from './components/delivery-options';
import CartAbandon from './components/cart-abandon';
import CSRComponent from './components/csr-component';
import PrefetchAssets from './components/prefetch-assets';
import { PayPalProvider } from './components/order-summary/checkout-container/paypal-button';

import useCartState, { useCartActions } from '../hooks/useCartState';
import useUserData, { isCallCenter } from '../hooks/useUserData';
import withPage from '../page';
import withPageComponents from '../page/components/with-page-components';
import './cart-page.less';

const EmptyCartRecommendations = ({ children }) => {
    const [hasRecs, setHasRecs] = useState(false);
    const { emptyCart } = useCartState();
    return children({
        emptyCart,
        onRecsReceived: () => {
            setHasRecs(true);
        },
        ...(emptyCart
            ? { cartTitle: hasRecs ? 'Your cart is empty but we got you covered' : 'Your Cart is empty' }
            : {}),
    });
};

//checks for changes in cart id and cartItemsLength to trigger a fetchCart call
const FetchCart = withPageComponents(({ vanityUrl }) => {
    const { setCartView, cartInfo } = useCartState();
    const { userData } = useUserData();
    const { updatedFromService } = userData;
    const { setLoginCookie } = useCartActions();
    const [isMounted, setIsMounted] = useState(false);
    const { id, cartView, recentlyAdded } = cartInfo;
    const { x_userType, orderItem } = cartView || {};
    const cartViewItemsLength = orderItem?.length || 0;
    //do not perform cookie check if user is in call center
    const disableCookieCheck = !updatedFromService || isCallCenter(userData);
    const isLoggedIn = id && x_userType === 'R';

    useEffect(() => {
        //for client side routing, wait till vanityUrl is set to cart before setting cartView to true
        if (id && !isMounted && vanityUrl === 'cart') {
            setIsMounted(true);
            setCartView(true);
        }
    }, [id, isMounted, vanityUrl]);
    useEffect(() => {
        //if cart items have changed outside of normal cart actions, trigger a fetchCart call (example empty cart recommendations)
        if (recentlyAdded && cartViewItemsLength === 0 && vanityUrl === 'cart') {
            setCartView(true);
        }
    }, [recentlyAdded, cartViewItemsLength, vanityUrl]);
    useEffect(() => {
        //if user is not logged in, set cookie to redirect to cart after login
        if (!isLoggedIn && !disableCookieCheck) {
            setLoginCookie();
        }
    }, [isLoggedIn, disableCookieCheck]);
    return null;
}, {});

const CartContainer = ({ vanityUrl }) => {
    return (
        <Container className="cart-container">
            <PrefetchAssets />
            <FetchCart />
            <PayPalProvider>
                <CSRComponent>
                    {({ csrActions, orderLevelOverrideComponent, deleteAllButton, palatizedShipping }) => {
                        return (
                            <>
                                <div className="product-summary">
                                    <EmptyCartRecommendations>
                                        {({ onRecsReceived, cartTitle }) => {
                                            return (
                                                <>
                                                    <CartHeader title={cartTitle} deleteAllButton={deleteAllButton} />
                                                    <CartItems
                                                        onRecsReceived={onRecsReceived}
                                                        csrActions={csrActions}
                                                    />
                                                </>
                                            );
                                        }}
                                    </EmptyCartRecommendations>
                                    <DeliveryOptions csrActions={csrActions} />
                                    <RewardsInfo palatizedShipping={palatizedShipping} csrActions={csrActions} />
                                    {orderLevelOverrideComponent}
                                    <ProductRecommendations vanityUrl={vanityUrl} />
                                </div>
                                <OrderSummary csrActions={csrActions} />
                            </>
                        );
                    }}
                </CSRComponent>
            </PayPalProvider>
            <CartAbandon />
        </Container>
    );
};

const getPath = (match, search) => {
    const {
        params: { dir },
    } = match;
    return dir;
};

export default withPage(CartContainer, {
    wrapperClassName: 'cart-root',
    showWordCloud: true,
    useV2: true,
    useGraphQL: true,
    derivedPath: getPath,
    derivedSlug: () => '',
    disablePlaceholder: true,
});
